import { useState, useEffect, useRef } from 'react';
import { 
  Shield, FileText, Check, AlertTriangle, Loader2, 
  ScrollText, User, Lock, Eye, Download, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TermsConditionsModal({ 
  user, 
  tenantName = "Your Organization",
  tenantId,
  onAccepted,
  onClose 
}) {
  const [tcContent, setTcContent] = useState('');
  const [typedName, setTypedName] = useState('');
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const scrollRef = useRef(null);

  // Fetch T&C content on mount
  useEffect(() => {
    fetchTCContent();
  }, [tenantName]);

  const fetchTCContent = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/api/terms-conditions/content?tenant_name=${encodeURIComponent(tenantName)}`
      );
      if (response.ok) {
        const data = await response.json();
        setTcContent(data.content);
      }
    } catch (error) {
      console.error('Failed to fetch T&C content:', error);
      toast.error('Failed to load Terms & Conditions');
    } finally {
      setLoading(false);
    }
  };

  // Track scroll position
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    
    if (scrollPercentage >= 0.95) {
      setHasScrolledToBottom(true);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!typedName.trim()) {
      toast.error('Please type your full name to confirm');
      return;
    }

    if (!hasScrolledToBottom) {
      toast.error('Please read the entire agreement before accepting');
      return;
    }

    if (!agreedToTerms) {
      toast.error('Please check the agreement checkbox');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Ensure we have all required fields
      const requestData = {
        user_id: user.id || user.user_id || '',
        user_name: user.name || user.full_name || '',
        user_email: user.email || '',
        typed_name: typedName.trim(),
        tenant_id: tenantId || 'default',
        tenant_name: tenantName || 'Your Organization'
      };
      
      console.log('T&C Accept Request:', requestData);
      
      const response = await fetch(`${API_URL}/api/terms-conditions/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Terms & Conditions accepted successfully');
        onAccepted?.(data);
      } else {
        // Properly extract error message
        const errorMsg = typeof data?.detail === 'string' 
          ? data.detail 
          : typeof data?.detail === 'object' 
            ? JSON.stringify(data.detail)
            : 'Failed to accept terms';
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('Failed to accept T&C:', error);
      // Ensure we always show a string message
      const errorMessage = error?.message && typeof error.message === 'string' 
        ? error.message 
        : 'Failed to submit agreement. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden border flex flex-col">
        {/* Header - Fixed */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 text-white flex-shrink-0">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-white/20 rounded-lg">
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Terms & Conditions Agreement</h2>
              <p className="text-blue-100 text-xs">{tenantName} - Insyra AI Platform</p>
            </div>
          </div>
          <div className="flex items-center gap-4 mt-2 text-sm">
            <Badge variant="secondary" className="bg-white/20 text-white border-0">
              <Lock className="h-3 w-3 mr-1" />
              Required
            </Badge>
            <span className="text-blue-100 text-xs">
              Please read and accept to continue
            </span>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* T&C Content - Scrollable */}
              <div className="relative">
                <div 
                  ref={scrollRef}
                  onScroll={handleScroll}
                  className="h-[200px] overflow-y-auto border rounded-lg p-3 bg-muted/30 font-mono text-xs leading-relaxed whitespace-pre-wrap"
                  data-testid="tc-content-scroll"
                >
                  {tcContent}
                </div>
                
                {/* Scroll indicator */}
                {!hasScrolledToBottom && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background to-transparent h-12 flex items-end justify-center pb-1 pointer-events-none">
                    <Button 
                      size="sm" 
                      variant="secondary"
                      onClick={scrollToBottom}
                      className="pointer-events-auto animate-bounce text-xs h-7"
                    >
                      <ChevronDown className="h-3 w-3 mr-1" />
                      Scroll to read more
                    </Button>
                  </div>
                )}
                
                {hasScrolledToBottom && (
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                    <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-xs">
                      <Check className="h-3 w-3 mr-1" />
                      Read Complete
                    </Badge>
                  </div>
                )}
              </div>

              {/* User Info & Signature */}
              <Card>
                <CardContent className="pt-3 pb-3">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Your Name</Label>
                      <p className="font-medium text-sm">{user?.name || user?.full_name}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Your Email</Label>
                      <p className="font-medium text-sm">{user?.email}</p>
                    </div>
                  </div>
                  
                  {/* Digital Signature */}
                  <div className="space-y-1">
                    <Label htmlFor="typed-name" className="flex items-center gap-2 text-sm">
                      <User className="h-4 w-4 text-blue-500" />
                      Digital Signature - Type Your Full Name
                    </Label>
                    <Input
                      id="typed-name"
                      placeholder="Type your full name exactly as shown above"
                      value={typedName}
                      onChange={(e) => setTypedName(e.target.value)}
                      className="text-base font-medium"
                      data-testid="tc-signature-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      By typing your name, you are providing your digital signature
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Agreement Checkbox */}
              <div className="flex items-start gap-3 p-3 border rounded-lg bg-amber-50/50 dark:bg-amber-900/10">
                <Checkbox 
                  id="agree-terms"
                  checked={agreedToTerms}
                  onCheckedChange={setAgreedToTerms}
                  disabled={!hasScrolledToBottom}
                  data-testid="tc-agree-checkbox"
                  className="mt-0.5"
                />
                <label 
                  htmlFor="agree-terms" 
                  className={`text-xs leading-relaxed cursor-pointer ${!hasScrolledToBottom ? 'text-muted-foreground' : ''}`}
                >
                  I have read, understood, and agree to be bound by the Terms and Conditions stated above. 
                  I acknowledge that this agreement is legally binding and that my digital signature 
                  carries the same weight as a physical signature.
                </label>
              </div>

              {/* Important Notice */}
              <div className="flex items-start gap-2 p-2 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-200 dark:border-red-800">
                <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-700 dark:text-red-300">
                  <strong>Important:</strong> Your acceptance will be recorded with timestamp and IP address. 
                  Violation of these terms may result in disciplinary action and legal proceedings.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer - Fixed */}
        <div className="border-t p-3 bg-muted/30 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Eye className="h-4 w-4" />
            <span className="hidden sm:inline">Your agreement will be saved and available for download</span>
          </div>
          
          <Button
            onClick={handleSubmit}
            disabled={!hasScrolledToBottom || !agreedToTerms || !typedName.trim() || isSubmitting}
            className="min-w-[150px]"
            data-testid="tc-submit-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Accept & Continue
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
